import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { InternalLinks } from '../common/enums/common';
import useServiceClients from '../common/hooks/useServiceClients';
import {
  CartActionType,
  CartStatusCodeEnum,
  ICartProductStatus,
  ProductStatusType,
} from '../container/cart/actions/action';
import { ICart } from '../container/cart/ICart';
import {
  CartReducer,
  ICartState,
  initialCartProductState,
} from '../container/cart/reducers/reducer';
import { StoreInfo } from '../container/location/components/hooks/useFetchStores';
import CartService from '../services/cart';
import { UserProfileProps, useUserProfile } from './user-provider';
import useBenefitStore from '../common/hooks/useBenefitStore';
import { splunk_message } from '../common/splunk_constant';
import useSplunkLogger from '../common/hooks/useSplunkLogger';

export type CartProps = {
  cart: ICart;
  cartLoading: boolean;
  selectStore: StoreInfo;
  setCart: React.Dispatch<React.SetStateAction<ICart>>;
  setSelectStore: React.Dispatch<React.SetStateAction<StoreInfo>>;
  getUpdatedCartInfo: () => void;
  cartState: ICartState;
  setProductStatus: (actionType: CartActionType, payload?: ICartProductStatus) => void;
  savedForLaterCart: ICart;
  setSavedForLaterCart: React.Dispatch<React.SetStateAction<ICart>>;
  isModalOpen: boolean;
  modalTitle: string;
  modalDescription: string;
  showCancel: boolean;
  showModal: (title: string, description: string, showCancel: boolean) => void;
  closeModal: () => void;
  cartMsg: { action: string; code: string } | undefined;
  setCartMsg: React.Dispatch<React.SetStateAction<{ action: string; code: string } | undefined>>;
  isLoaderEnabled: () => boolean;
  isBestSavingsModalOpen: boolean;
  setIsBestSavingsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hasCartTotalMismatch: boolean;
  setHasCartTotalMismatch: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CartContext = createContext<CartProps | null>({} as CartProps);

export function useCartProvider() {
  const ctx = useContext(CartContext);
  return ctx;
}

const initialCartState = {
  cartItemsCount: 0,
  totalLineItemQuantity: 0,
  savingsPlanDiscount: '',
  shipping: '',
  subTotal: '',
  additionalBenefits: [],
  cartItems: [],
  savingsPlans: [],
  promotions: '',
  total: '',
  savings: '',
  benefits: '',
  employeeDiscount: '',
  customLineItems: [],
};

const initialStoreState = {
  id: '',
  name: '',
  geoCode: '',
  channelId: '',
};

export default function CartProvider({ children }: { children: ReactNode }): JSX.Element {
  const { userProfile } = useUserProfile();
  const [cart, setCart] = useState<ICart>(initialCartState);
  const [savedForLaterCart, setSavedForLaterCart] = useState<ICart>({} as ICart);
  const [selectStore, setSelectStore] = useState<any>(initialStoreState);
  const cartLoadedForUserRef = useRef<string | null>(null);
  const [cartLoading, setCartLoading] = useState(true);
  const { splunkLogger } = useSplunkLogger();

  const pricingRequiredPages = [
    InternalLinks.cart,
    InternalLinks.checkout,
    InternalLinks.lensWizard,
  ];

  const isPricingRequiredPage = () => {
    const currentPageURL = globalThis.location.href.toLowerCase();
    return pricingRequiredPages.some(url => currentPageURL.includes(url));
  };

  const isLoaderEnabled = () => isPricingRequiredPage();
  const { visionBffClient, ctproxyBffClient } = useServiceClients(isLoaderEnabled());

  const [state, dispatch] = useReducer(CartReducer, initialCartProductState);
  const cartService = new CartService(visionBffClient,splunkLogger);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const [cartMsg, setCartMsg] = useState<{ action: string; code: string }>();
  const { selectedBenefit } = useBenefitStore();

  const [isBestSavingsModalOpen, setIsBestSavingsModalOpen] = useState(false);
  const [hasCartTotalMismatch, setHasCartTotalMismatch] = useState(false);

  useEffect(() => {
    const userGuid = userProfile?.guid;

    if (userGuid && cartLoadedForUserRef.current !== userGuid) {
      cartLoadedForUserRef.current = userGuid;
      getCart();
    }
  }, [userProfile]);

  const getCart = async () => {
    setCartLoading(true);
    try {
      let res;

      if (selectedBenefit && isPricingRequiredPage()) {
        res = await cartService.updateCartForPricing(selectedBenefit);
      } else {
        res = await cartService.getCart();
      }

      if (!res || res?.error) {
        return handleError(CartActionType.GET_CART, res?.error);
      }

      if (res && res.total && res.receivedTotal && res.total !== res.receivedTotal) {
        splunkLogger({
          logType: 'error',
          data: {
            ...splunk_message.ERROR.CART_TOTAL_MISMATCH_ERROR,
            message: `Cart total mismatch detected: UI shows ${res.total}, backend shows ${res.receivedTotal}`,
          },
        });

        setHasCartTotalMismatch(true);
      } else {
        setHasCartTotalMismatch(false);
      }

      if (res.isBestSavingsModalOpen) {
        setIsBestSavingsModalOpen(true);
      }
      setCart(res);
      if (state.productStatus.action === CartActionType.GET_CART) {
        setProductStatus(CartActionType.CLOSE_MODEL);
      }
    } finally {
      setCartLoading(false);
    }
  };

  const setProductStatus = (actionType: CartActionType, payload?: ICartProductStatus) => {
    dispatch({ type: actionType, payload: payload || initialCartProductState.productStatus });
  };

  const getUpdatedCartInfo = async () => {
    const res = await cartService.getCart();
    if (!res || res?.error) {
      return null;
    }

    if (res && res.total && res.receivedTotal && res.total !== res.receivedTotal) {
      splunkLogger({
        logType: 'error',
        data: {
          ...splunk_message.ERROR.CART_TOTAL_MISMATCH_ERROR,
          message: `Cart total mismatch detected: UI shows ${res.total}, backend shows ${res.receivedTotal}`,
        },
      });
      setHasCartTotalMismatch(true);
    } else {
      setHasCartTotalMismatch(false);
    }

    setCart(res);
    return res;
  };

  const handleError = (actionType: CartActionType, error: any) => {
    const generalFailurePayload = {
      action: actionType,
      type: ProductStatusType.WARNINING,
      title: CartStatusCodeEnum.GENERAL_CART_FAILURE_TITLE,
      description: CartStatusCodeEnum.GENERAL_CART_FAILURE_DESCRIPTION,
      error: error,
    };
    splunkLogger({
      logType: 'error',
      data: { ...splunk_message.ERROR.CART_ERROR, actionType, error },
    });

    setProductStatus(actionType, generalFailurePayload);
  };

  // Modal control functions
  const showModal = (title: string, description: string, showCancel: boolean) => {
    setModalTitle(title);
    setModalDescription(description);
    setIsModalOpen(true);
    setShowCancel(showCancel);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <CartContext.Provider
      value={{
        cartState: state,
        cart,
        cartLoading,
        selectStore,
        savedForLaterCart,
        setProductStatus,
        setCart,
        setSelectStore,
        getUpdatedCartInfo,
        setSavedForLaterCart,
        isModalOpen,
        modalTitle,
        modalDescription,
        showCancel,
        showModal,
        closeModal,
        cartMsg,
        setCartMsg,
        isLoaderEnabled,
        isBestSavingsModalOpen,
        setIsBestSavingsModalOpen,
        hasCartTotalMismatch,
        setHasCartTotalMismatch,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
